import { FormEvent, useState } from 'react'
import { amountInput, formatTRY, kurusToLiraInput, liraToKurus } from '../../utils/money'
import { UndoIcon, XIcon } from '../icons'
import './PriceDialog.css'

interface Props {
  productName: string
  unitPriceKurus: number
  /** Menüdeki fiyat; ürün menüden kalkmışsa bilinmiyor. */
  menuPriceKurus?: number
  qty: number
  onConfirm: (unitPriceKurus: number) => void
  onCancel: () => void
}

/**
 * Changes the unit price of one cart line.
 *
 * Items sold by weight don't have a fixed price, the menu figure is a starting
 * point and the scale decides. Only this line changes: the menu keeps its own
 * price, so the next customer is quoted the listed one again.
 *
 * Menü fiyatı üstte bir kutuda (kullanıcı, 26 Eki 2026): kasiyer yazdığı
 * rakamın menüden ne kadar saptığını görsün; "Menü fiyatına dön" tek dokunuş.
 */
export function PriceDialog({
  productName,
  unitPriceKurus,
  menuPriceKurus,
  qty,
  onConfirm,
  onCancel,
}: Props) {
  const [input, setInput] = useState(kurusToLiraInput(unitPriceKurus))

  const parsed = liraToKurus(input)
  const valid = parsed !== null && parsed >= 0
  const diff = valid && menuPriceKurus !== undefined ? parsed - menuPriceKurus : 0
  const pct = menuPriceKurus ? Math.round((Math.abs(diff) / menuPriceKurus) * 100) : null

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!valid) return
    onConfirm(parsed)
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true" aria-label={`${productName} fiyatı`}>
      <form className="dlg__card price" onSubmit={submit}>
        <div className="price__head">
          <h2>{productName}</h2>
          <button type="button" className="price__x" onClick={onCancel} aria-label="Kapat">
            <XIcon />
          </button>
        </div>

        {menuPriceKurus !== undefined && (
          <div className="price__menu">
            <span>Menü fiyatı</span>
            <strong>{formatTRY(menuPriceKurus)}</strong>
          </div>
        )}

        <label className="price__field" htmlFor="price-input">
          <span>Bu satırın birim fiyatı</span>
          <div className="price__input">
            <input
              id="price-input"
              value={input}
              onChange={(e) => setInput(amountInput(e.target.value))}
              inputMode="decimal"
              autoFocus
              onFocus={(e) => e.target.select()}
            />
            <em>₺</em>
          </div>
        </label>

        {menuPriceKurus !== undefined && valid && (
          <div className={`price__diff ${diff === 0 ? 'is-same' : diff > 0 ? 'is-up' : 'is-down'}`}>
            <span>
              {diff === 0
                ? 'Menü fiyatıyla aynı'
                : `Menü fiyatından ${formatTRY(Math.abs(diff))} ${diff > 0 ? 'fazla' : 'eksik'}${pct ? ` (%${pct})` : ''}`}
            </span>
            {diff !== 0 && (
              <button type="button" onClick={() => setInput(kurusToLiraInput(menuPriceKurus))}>
                <UndoIcon />
                Menü fiyatına dön
              </button>
            )}
          </div>
        )}

        <div className="price__total">
          <span>{qty} adet toplam</span>
          <strong>{valid ? formatTRY(parsed * qty) : '-'}</strong>
        </div>

        <p className="price__hint">Yalnızca bu satır için geçerli. Menüdeki fiyat değişmez.</p>

        <div className="dlg__buttons">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Vazgeç
          </button>
          <button type="submit" className="btn btn--primary" disabled={!valid}>
            Tamam
          </button>
        </div>
      </form>
    </div>
  )
}
